import { useState } from "react";
import {
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography, Box, Paper, Button,
    TextField, MenuItem, TablePagination, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions, Divider, Chip,
    CircularProgress, useMediaQuery // Import CircularProgress and useMediaQuery
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faTicketSimple } from "@fortawesome/free-solid-svg-icons"; // Added faTicketSimple for empty state
import baseURL from "../../config";
import { toast } from "react-toastify";

// Custom COLORS for a Microsoft-like aesthetic
const MS_COLORS = {
    primary: '#2D4A3D',       // Microsoft's signature blue
    primaryLight: '#EBF5EE',  // Lighter shade for active background
    text: '#323130',          // Dark gray for main text
    textSecondary: '#605D5C', // Lighter gray for secondary text
    background: '#FFFFFF',    // Pure white background
    backgroundLight: '#F3F2F1', // Light gray for subtle accents
    border: '#E1E1E1',        // Light gray for borders
    hoverBackground: '#F0F0F0', // Very light gray on hover
    white: '#FFFFFF',
    accent: '#107C10',        // A green for success/resolved
    red: '#D83B01',           // Microsoft's red for errors/open status
};

const TicketsManagement = ({ tickets, onUpdate }) => { // Component now receives props
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [open, setOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [resolvingTicketId, setResolvingTicketId] = useState(null);

    const isSmallScreen = useMediaQuery("(max-width:600px)");

    // Data fetching logic is removed, now it's a prop
    // The `tickets` state is no longer managed here

    const handleMarkResolved = async (ticketId) => {
        setResolvingTicketId(ticketId);
        
        try {
            const response = await fetch(
                `${baseURL}/admin/tickets/${ticketId}/resolve`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ status: 'Resolved' }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update ticket status');
            }

            toast.success('Ticket marked as resolved successfully!');
            onUpdate(); // Call the parent function to re-fetch and update the UI
        } catch (error) {
            console.error('Error resolving ticket:', error);
            toast.error(error.message || 'Could not resolve ticket. Please try again.');
        } finally {
            setResolvingTicketId(null);
        }
    };

    // Open ticket details dialog
    const handleOpenDialog = (ticket) => {
        setSelectedTicket(ticket);
        setOpen(true);
    };

    // Close ticket details dialog
    const handleClose = () => {
        setOpen(false);
        setSelectedTicket(null);
    };

    // Filtering tickets based on search and status
    const filteredTickets = tickets.filter((ticket) => {
        const matchesSearch = ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              ticket.raised_by.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === "all" || ticket.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    // Handle page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    
    // No need for a separate loading state in this component
    const hasTickets = tickets.length > 0;
    const hasFilteredTickets = filteredTickets.length > 0;

    return (
        <Box
            sx={{
                backgroundColor: "#fff",
                borderRadius: "16px",
                boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                border: "1px solid rgba(0,0,0,0.05)",
                p: 3,
                mt: 3,
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    color: "#1a1a2e",
                    mb: 3,
                    fontWeight: 800,
                    fontSize: isSmallScreen ? "1.05rem" : "1.2rem",
                    letterSpacing: "-0.3px",
                }}
            >
                Tickets Management
            </Typography>

            {/* Search & Filter Fields */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center", flexDirection: isSmallScreen ? "column" : "row" }}>
                <TextField
                    variant="outlined"
                    placeholder="Search by subject or user..."
                    size="small"
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                        flex: 1,
                        borderRadius: "4px",
                        backgroundColor: MS_COLORS.white,
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "4px",
                            boxShadow: "none",
                            fontSize: "0.85rem",
                            "& fieldset": { borderColor: MS_COLORS.border },
                            "&:hover fieldset": { borderColor: MS_COLORS.primary },
                            "&.Mui-focused fieldset": { borderColor: MS_COLORS.primary, borderWidth: "2px" }
                        },
                        "& .MuiInputLabel-root": { fontSize: "0.85rem" },
                        width: isSmallScreen ? '100%' : 'auto'
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: MS_COLORS.textSecondary, fontSize: "1.1rem" }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    select
                    label="Filter by Status"
                    variant="outlined"
                    size="small"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{
                        minWidth: 180,
                        borderRadius: "4px",
                        backgroundColor: MS_COLORS.white,
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "4px",
                            fontSize: "0.85rem",
                            "& fieldset": { borderColor: MS_COLORS.border },
                            "&:hover fieldset": { borderColor: MS_COLORS.primary },
                            "&.Mui-focused fieldset": { borderColor: MS_COLORS.primary, borderWidth: "2px" }
                        },
                        "& .MuiInputLabel-root": { fontSize: "0.85rem", color: MS_COLORS.textSecondary },
                        width: isSmallScreen ? '100%' : 'auto'
                    }}
                    SelectProps={{
                        MenuProps: {
                            PaperProps: {
                                sx: {
                                    bgcolor: MS_COLORS.background,
                                    borderRadius: '4px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    mt: 0.5,
                                },
                            },
                        },
                    }}
                >
                    <MenuItem value="all" sx={{ fontSize: "0.85rem", color: MS_COLORS.text }}>All Tickets</MenuItem>
                    <MenuItem value="Open" sx={{ fontSize: "0.85rem", color: MS_COLORS.text }}>Open</MenuItem>
                    <MenuItem value="Resolved" sx={{ fontSize: "0.85rem", color: MS_COLORS.text }}>Resolved</MenuItem>
                </TextField>
            </Box>

            {/* Tickets Table */}
            {!hasTickets ? (
                <Box
                    sx={{
                        textAlign: "center",
                        mt: 3,
                        p: 3,
                        border: `1px dashed ${MS_COLORS.border}`,
                        borderRadius: 1,
                        bgcolor: MS_COLORS.backgroundLight,
                    }}
                >
                    <FontAwesomeIcon icon={faTicketSimple} style={{ fontSize: "40px", color: MS_COLORS.textSecondary, marginBottom: "8px" }} />
                    <Typography sx={{ color: MS_COLORS.textSecondary, fontSize: "0.9rem", fontStyle: "italic" }}>
                        There are no tickets to display at the moment.
                    </Typography>
                </Box>
            ) : !hasFilteredTickets ? (
                <Box
                    sx={{
                        textAlign: "center",
                        mt: 3,
                        p: 3,
                        border: `1px dashed ${MS_COLORS.border}`,
                        borderRadius: 1,
                        bgcolor: MS_COLORS.backgroundLight,
                    }}
                >
                    <FontAwesomeIcon icon={faTicketSimple} style={{ fontSize: "40px", color: MS_COLORS.textSecondary, marginBottom: "8px" }} />
                    <Typography sx={{ color: MS_COLORS.textSecondary, fontSize: "0.9rem", fontStyle: "italic" }}>
                        No tickets found matching your criteria.
                    </Typography>
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 1, boxShadow: 0, border: `1px solid ${MS_COLORS.border}` }}>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead sx={{ bgcolor: MS_COLORS.backgroundLight }}>
                            <TableRow>
                                {["S/N", "Subject", "Raised by", "Status", "Created At", "Actions"].map((head) => (
                                    <TableCell key={head} sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>
                                        {head}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredTickets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((ticket, index) => (
                                <TableRow
                                    key={ticket.id}
                                    sx={{
                                        "&:hover": { backgroundColor: MS_COLORS.hoverBackground },
                                    }}
                                >
                                    <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>{page * rowsPerPage + index + 1}</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>{ticket.subject}</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>{ticket.raised_by}</TableCell>

                                    <TableCell sx={{ fontSize: "0.8rem" }}>
                                        <Chip
                                            label={ticket.status}
                                            size="small"
                                            sx={{
                                                backgroundColor: ticket.status === "Resolved" ? MS_COLORS.accent + '20' : MS_COLORS.red + '20',
                                                color: ticket.status === "Resolved" ? MS_COLORS.accent : MS_COLORS.red,
                                                fontWeight: 'bold',
                                                fontSize: '0.7rem',
                                                height: '24px',
                                                borderRadius: '4px',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>{new Date(ticket.created_at).toLocaleString()}</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem" }}>
                                        <Box display="flex" gap={1} alignItems="center">
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={() => handleOpenDialog(ticket)}
                                                sx={{
                                                    fontSize: "0.75rem",
                                                    color: MS_COLORS.primary,
                                                    textDecoration: "underline",
                                                    padding: "4px 8px",
                                                    minWidth: "auto",
                                                    textTransform: "none",
                                                    "&:hover": {
                                                        backgroundColor: MS_COLORS.primaryLight + '30',
                                                        textDecoration: "underline",
                                                    },
                                                }}
                                            >
                                                View Details
                                            </Button>

                                            {ticket.status !== "Resolved" && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleMarkResolved(ticket.id)}
                                                    disabled={resolvingTicketId === ticket.id}
                                                    sx={{
                                                        textTransform: "none",
                                                        fontSize: "0.75rem",
                                                        borderRadius: "4px",
                                                        borderColor: MS_COLORS.accent,
                                                        color: MS_COLORS.accent,
                                                        "&:hover": {
                                                            backgroundColor: MS_COLORS.accent + '10',
                                                            borderColor: MS_COLORS.accent,
                                                        },
                                                        "&:disabled": {
                                                            borderColor: MS_COLORS.border,
                                                            color: MS_COLORS.textSecondary,
                                                        },
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    {resolvingTicketId === ticket.id ? (
                                                        <CircularProgress size={16} sx={{ color: MS_COLORS.accent }} />
                                                    ) : (
                                                        <FontAwesomeIcon icon={faCheckCircle} />
                                                    )}
                                                    Mark Resolved
                                                </Button>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Pagination */}
            {hasFilteredTickets && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredTickets.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-actions":
                        {
                            fontSize: "0.8rem",
                            color: MS_COLORS.textSecondary,
                        },
                    }}
                />
            )}

            {/* Dialog for Ticket Details */}
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: { borderRadius: "8px", p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" },
                }}
            >
                <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.2rem", color: MS_COLORS.text }}>
                    Ticket Details
                </DialogTitle>
                <Divider sx={{ mb: 2 }} />
                <DialogContent sx={{ py: 2 }}>
                    {selectedTicket && (
                        <Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, alignItems: 'center' }}>
                                <Typography variant="body1" sx={{ color: MS_COLORS.text }}><strong>ID:</strong> {selectedTicket.id}</Typography>
                                <Chip
                                    label={selectedTicket.status}
                                    sx={{
                                        backgroundColor: selectedTicket.status === "Resolved" ? MS_COLORS.accent + '20' : MS_COLORS.red + '20',
                                        color: selectedTicket.status === "Resolved" ? MS_COLORS.accent : MS_COLORS.red,
                                        fontWeight: 'bold',
                                        fontSize: '0.75rem',
                                        height: '26px',
                                        borderRadius: '4px',
                                    }}
                                />
                            </Box>
                            <Typography variant="body1" sx={{ mb: 1, color: MS_COLORS.text }}><strong>Issue Category:</strong> {selectedTicket.subject}</Typography>
                            <Typography variant="body1" sx={{ mb: 1, color: MS_COLORS.text }}><strong>Raised By:</strong> {selectedTicket.raised_by}</Typography>
                            <Typography variant="body1" sx={{ mb: 1, color: MS_COLORS.text }}><strong>Created At:</strong> {new Date(selectedTicket.created_at).toLocaleString()}</Typography>
                            {selectedTicket.status === "Resolved" && selectedTicket.date_resolved && (
                                <Typography variant="body1" sx={{ mb: 1, color: MS_COLORS.text }}>
                                    <strong>Resolved At:</strong> {new Date(selectedTicket.date_resolved).toLocaleString()}
                                </Typography>
                            )}
                            <Typography variant="body1" sx={{ mt: 2, backgroundColor: MS_COLORS.backgroundLight, p: 2, borderRadius: 1, color: MS_COLORS.text }}>
                                <strong>Description:</strong> {selectedTicket.description}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <Divider sx={{ mt: 2 }} />
                <DialogActions sx={{ justifyContent: "flex-end", pt: 2, px: 3 }}>
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        sx={{
                            textTransform: "none",
                            borderRadius: "4px",
                            backgroundColor: MS_COLORS.primary,
                            color: MS_COLORS.white,
                            "&:hover": { backgroundColor: MS_COLORS.primary, opacity: 0.9 },
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TicketsManagement;